import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSidebar } from '../../contexts/SidebarContext';
import { useAdmin } from '../../contexts/AdminContext';
import PageHeader from './PageHeader';
import PremiumUpsell from './PremiumUpsell';

/**
 * FeatureUpsellPage — vitrine de conversão para recursos premium SEM amostra
 * de dados própria (Vídeos Virais, Insights de Canal...). Renderiza o main
 * padrão com o header da página, uma fileira de cards FAKE borrados (sugere
 * a interface por trás do bloqueio) e o card de venda PremiumUpsell.
 *
 * Props:
 *   icon: ReactNode        — ícone do PageHeader (normalizado pelo próprio)
 *   title / subtitle       — strings JÁ traduzidas
 *   upsell: { badge, headline, subhead, bullets: [{Icon,text}], ctaLabel }
 */
const FeatureUpsellPage = ({ icon, title, subtitle, upsell }) => {
  const { t } = useTranslation();
  const { sidebarWidth, isMobile } = useSidebar();
  const { subscriptionData } = useAdmin();

  // Card fake (placeholder puro — nenhum dado real) p/ a fileira borrada.
  const FakeCard = () => (
    <div style={{
      background: 'var(--dp-bg-card)',
      border: '1px solid var(--dp-border-subtle)',
      borderRadius: 12,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className="dp-skeleton" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="dp-skeleton" style={{ height: 12, borderRadius: 4, width: '70%' }} />
          <div className="dp-skeleton" style={{ height: 9, borderRadius: 4, width: '45%' }} />
        </div>
      </div>
      <div className="dp-skeleton" style={{ width: '100%', aspectRatio: '16 / 7', borderRadius: 8 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="dp-skeleton" style={{ flex: 1, height: 40, borderRadius: 8 }} />
        ))}
      </div>
    </div>
  );

  return (
    <main
      className="dp-main-content dp-fade-in-up"
      style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--dp-font)',
      }}
    >
      {/* Header padrão da página + pill "Prévia" */}
      <PageHeader
        icon={icon}
        title={title}
        description={subtitle}
        titleBadge={t('viralNiches.teaser_preview_badge')}
      />

      {/* Fileira FAKE borrada — sugere a interface, sem nenhum dado real */}
      <div style={{ position: 'relative', marginTop: 8 }} aria-hidden="true">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 12,
          filter: 'blur(7px)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {[0, 1, 2].map((i) => <FakeCard key={i} />)}
        </div>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 0%, var(--dp-bg-primary) 78%)',
          pointerEvents: 'none',
        }} />
      </div>

      {/* Card de venda — sobrepõe o fim do borrão */}
      <div style={{ position: 'relative', zIndex: 2, marginTop: '-64px', paddingBottom: 32 }}>
        <PremiumUpsell
          badge={upsell.badge}
          headline={upsell.headline}
          subhead={upsell.subhead}
          bullets={upsell.bullets}
          ctaLabel={upsell.ctaLabel}
          planType={subscriptionData?.plan_type}
        />
      </div>
    </main>
  );
};

export default FeatureUpsellPage;
